// Topic → catch-up target (the `(kind, topicId)` pair, plus any extra query
// params) for the generic snapshot catch-up. Split out of snapshot-store.ts (BL-2491).

import { staffUnreadsTopicId } from '../../isomorphic/realtime/staff-unreads-topic-id';

/** A trailing `:snapshot:<32-hex HMAC>` — the private-topic token, BUG-1938. */
const PRIVATE_TOPIC_TOKEN_SUFFIX = /(?<=:snapshot):[0-9a-f]{32}$/;

/**
 * Parse a snapshot topic into the `(kind, topicId)` pair the catch-up
 * URL needs. Topic format from `topics.ts`:
 *
 *   `<scope>:<id>[:<aspect>...]:<snapshot>`
 *
 * Examples:
 *
 *   `user:{userId}:registrations:snapshot` → kind `user_registrations_snapshot`, id `{userId}`
 *   `lot:{lotId}:snapshot`                 → kind `lot_snapshot`, id `{lotId}`
 *
 * The `kind` is the topic with the id stripped, joined with `_`, and
 * any `-` (kebab) lowered to `_` (snake) so it lines up with the table
 * name + the trampoline route path.
 */
export function parseSnapshotTopic(topic: string): {
    kind: string;
    topicId: string;
    /** Extra catch-up query parameters, for a topic whose route needs them. */
    params?: Record<string, string>;
} | null {
    const staff = parseStaffUnreadsTopic(topic);
    if (staff) return staff;
    // A private bidder topic (BUG-1938) ends in an HMAC segment after its
    // `snapshot` kind. The token is a credential, not routing: dropping it keeps
    // the catch-up URL (`customer_user_unreads_snapshot`, `topic_id={id}`)
    // exactly what it was, and the route re-authorizes from the session.
    const segments = topic.replace(PRIVATE_TOPIC_TOKEN_SUFFIX, '').split(':');
    if (segments.length < 3 || segments[segments.length - 1] !== 'snapshot') {
        return null;
    }
    const topicId = segments[1];
    if (!topicId) return null;
    const kindSegments = segments
        .filter((_, i) => i !== 1)
        .map((s) => s.replace(/-/g, '_'));
    return { kind: kindSegments.join('_'), topicId };
}

/** `staff-user:{id}:tenant:{tenantId}:unreads:snapshot[:token]` (BL-2491). */
const STAFF_UNREADS_TOPIC = /^staff-user:([^:]+):tenant:([^:]+):unreads:snapshot(?::[0-9a-f]{32})?$/;

/**
 * The staff unread-count topic names a person AND a house, while its snapshot
 * row is keyed by the name-based UUID of that pair (ADR 056 — Delivery Record
 * § 9(c)). So the generic rule (segment 1 is the key, the rest is the kind)
 * would build a kind carrying the tenant's id and send the person's id as the
 * key. This reads the two ids out of the topic, computes the row key with the
 * same function the writer and the route use, and passes the house as
 * `tenant_id` for the route gate. The route never trusts the key: its
 * `authorize` recomputes it from the session and the gated house.
 *
 * @param topic - any snapshot topic.
 * @returns the catch-up target, or `null` when the topic is not the staff count.
 */
function parseStaffUnreadsTopic(topic: string): { kind: string; topicId: string; params: Record<string, string> } | null {
    const match = STAFF_UNREADS_TOPIC.exec(topic);
    if (!match) return null;
    const [, staffUserId, tenantId] = match;
    return {
        kind: 'staff_user_unreads_snapshot',
        topicId: staffUnreadsTopicId({ tenantId, staffUserId }),
        params: { tenant_id: tenantId },
    };
}
